/**
 * The shared-funder map a reader can trace. Funders left, members right, the
 * anchor between; hovering or focusing a node lights the edges it is on and
 * dims the rest, clicking pins it, and the panel beside the drawing lists what
 * the active node shares. The controls narrow the drawing by the minimum
 * shared count, show the intermediaries set aside, and search; the compact
 * variant on an organization page drops the controls and caps each column.
 * Below the medium breakpoint no drawing can be read, compact included, so
 * every map becomes two stacked lists there.
 *
 * The drawing is one tab stop: the arrow keys walk its nodes, so a map of a
 * hundred nodes is not a hundred tab presses. The drawing is a group rather
 * than an image so the nodes are reachable, and the lists on the page stay
 * as the equivalent for anyone the drawing does not serve. Motion is under
 * `motion-safe:` only.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { CoalitionMapItem } from '../../../src/serve/items.js';
import { Link } from '../components.js';
import { COPY } from '../copy.js';
import { formatCount, formatEin } from '../format.js';
import { ANCHOR_KEY, layoutSharedFunders, nextNodeKey, recipeParameters, type Direction, type MapLayout, type MapNode } from './layout.js';
import { FILL, LABEL_FILL } from './palette.js';
import { SharedFunderList } from './SharedFunderList.js';

const COMPACT_LIMIT = 8;
const LABEL_LENGTH = 34;
const LABEL_GAP = 12;

const truncate = (label: string): string => (label.length > LABEL_LENGTH ? `${label.slice(0, LABEL_LENGTH - 1)}…` : label);

const ARROWS: Readonly<Record<string, Direction>> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

const describe = (node: MapNode, anchor: string): string => {
  switch (node.kind) {
    case 'anchor':
      return `the anchor, reached by ${node.degree} counted funders`;
    case 'funder':
      return `reaches ${node.degree} of the organizations shown`;
    case 'member':
      return `shares ${node.degree} funders with ${anchor}`;
    case 'excluded':
      return 'set aside: reaches more organizations than the ceiling';
  }
};

const neighbours = (layout: MapLayout, key: string): MapNode[] => {
  // No edge names the anchor; what it connects to is every counted funder.
  if (key === ANCHOR_KEY) {
    return [...layout.funders];
  }
  const keys = new Set<string>();
  for (const edge of layout.edges) {
    if (edge.funder === key) {
      keys.add(edge.member);
    } else if (edge.member === key) {
      keys.add(edge.funder);
    }
  }
  return [...layout.funders, ...layout.members].filter((node) => keys.has(node.key));
};

const PartyName = ({ node }: { node: MapNode }) =>
  node.ein !== null && node.listed ? (
    <Link to={node.kind === 'funder' ? `/funder/${node.ein}` : `/org/${node.ein}`} title={node.name ?? undefined}>
      {node.label}
    </Link>
  ) : (
    <span title={node.name ?? undefined}>{node.label}</span>
  );

const Detail = ({ layout, node, onExplore }: { layout: MapLayout; node: MapNode | null; onExplore?: ((ein: string) => void) | undefined }) => {
  if (node === null) {
    return <p className="text-sm text-stone-500">{COPY.map.emptyDetail}</p>;
  }
  const linked = neighbours(layout, node.key);
  return (
    <div className="text-sm" data-testid="map-detail">
      <div className="font-medium text-stone-900">
        <PartyName node={node} />
      </div>
      {node.ein !== null ? <div className="text-xs text-stone-500">{formatEin(node.ein)}</div> : null}
      <p className="mt-1 text-stone-700">{describe(node, layout.anchor.label)}.</p>
      {node.kind === 'member' && node.ein !== null && node.listed && onExplore !== undefined ? (
        <button type="button" onClick={() => onExplore(node.ein ?? '')} className="mt-2 rounded border border-teal-700 px-2 py-1 text-teal-800 hover:bg-teal-50">
          Explore from here
        </button>
      ) : null}
      {linked.length > 0 ? (
        <ul className="mt-2 max-h-72 space-y-0.5 overflow-y-auto">
          {linked.map((other) => (
            <li key={other.key}>
              <PartyName node={other} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const SharedFunderMap = ({ map, compact = false, onExplore }: { map: CoalitionMapItem; compact?: boolean; onExplore?: (ein: string) => void }) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  // The slider starts at the recipe's own minimum and cannot go beneath it: no member of the item shares fewer.
  const floor = recipeParameters(map.parameters).minimumShared;
  const [minimumShared, setMinimumShared] = useState(floor);
  const [showExcluded, setShowExcluded] = useState(false);
  const [query, setQuery] = useState('');
  // The node last focused: the drawing's one tab stop, so leaving and returning lands where the reader was.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const drawing = useRef<SVGSVGElement>(null);
  const layout = useMemo(
    () => layoutSharedFunders(map, compact ? { limit: COMPACT_LIMIT } : { minimumShared, showExcluded, query }),
    [map, compact, minimumShared, showExcluded, query],
  );
  const { nodes, byKey } = useMemo(() => {
    const all = [layout.anchor, ...layout.funders, ...layout.members, ...layout.excluded];
    return { nodes: all, byKey: new Map(all.map((node) => [node.key, node])) };
  }, [layout]);
  // A pin can outlive its node: the slider or the search drops the pinned member and the key names
  // nothing drawn. Every edge would then stay dim and hovering would change nothing, because a pin
  // wins over a hover. The pin counts only while the layout holds the node, and it is cleared so the
  // list variant and aria-pressed agree with the drawing.
  const pinnedNode = pinned === null ? null : (byKey.get(pinned) ?? null);
  useEffect(() => {
    if (pinned !== null && pinnedNode === null) {
      setPinned(null);
    }
  }, [pinned, pinnedNode]);
  const active = pinnedNode === null ? hovered : pinned;
  const activeNode = active === null ? null : (byKey.get(active) ?? null);
  const stop = focusKey !== null && byKey.has(focusKey) ? focusKey : (layout.funders[0]?.key ?? layout.anchor.key);
  const incident = (edge: { funder: string; member: string }): boolean => active !== null && (edge.funder === active || edge.member === active || active === ANCHOR_KEY);
  const toggle = (key: string): void => setPinned((current) => (current === key ? null : key));
  const onKey = (key: string) => (event: KeyboardEvent<SVGGElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(key);
      return;
    }
    const direction = ARROWS[event.key];
    if (direction === undefined) {
      return;
    }
    event.preventDefault();
    const next = nextNodeKey(layout, key, direction);
    if (next !== null) {
      drawing.current?.querySelector<SVGGElement>(`[data-node="${next}"]`)?.focus();
    }
  };
  const node = (entry: MapNode) => {
    const isActive = active === entry.key;
    const labelX = entry.kind === 'member' ? entry.x + LABEL_GAP : entry.x - LABEL_GAP;
    return (
      <g
        key={entry.key}
        role="button"
        tabIndex={entry.key === stop ? 0 : -1}
        aria-pressed={pinned === entry.key}
        aria-label={`${entry.label}, ${describe(entry, layout.anchor.label)}`}
        data-node={entry.key}
        data-active={isActive ? 'true' : 'false'}
        className="cursor-pointer"
        onMouseEnter={() => setHovered(entry.key)}
        onMouseLeave={() => setHovered(null)}
        onFocus={() => {
          setHovered(entry.key);
          setFocusKey(entry.key);
        }}
        onBlur={() => setHovered(null)}
        onClick={() => toggle(entry.key)}
        onKeyDown={onKey(entry.key)}
      >
        <circle
          className="motion-safe:transition-all"
          cx={entry.x}
          cy={entry.y}
          r={isActive ? entry.r + 2 : entry.r}
          fill={FILL[entry.kind]}
          stroke={isActive ? '#1c1917' : 'none'}
          strokeWidth={1.5}
          strokeDasharray={entry.kind === 'excluded' ? '2 2' : undefined}
        />
        <text
          x={entry.kind === 'anchor' ? entry.x : labelX}
          y={entry.kind === 'anchor' ? entry.y - 16 : entry.y + 4}
          textAnchor={entry.kind === 'member' ? 'start' : entry.kind === 'anchor' ? 'middle' : 'end'}
          fontSize={entry.kind === 'anchor' ? 13 : 12}
          fontWeight={isActive || entry.kind === 'anchor' ? 600 : 400}
          fill={LABEL_FILL[entry.kind]}
        >
          {truncate(entry.label)}
        </text>
      </g>
    );
  };
  return (
    <div
      className="mt-2"
      onKeyDown={(event) => {
        // Escape in the search box clears the box, which is the browser's own behaviour, and nothing else.
        if (event.key === 'Escape' && !(event.target instanceof HTMLInputElement)) {
          setPinned(null);
        }
      }}
    >
      {compact ? null : (
        <div className="mb-3 flex flex-wrap items-end gap-4 text-sm" data-testid="map-controls">
          <label className="block">
            <span className="block text-stone-600">
              Shared funders at least <output className="font-medium text-stone-900">{minimumShared}</output>
            </span>
            <input
              type="range"
              min={floor}
              max={Math.max(floor, layout.maximumShared)}
              value={minimumShared}
              onChange={(event) => setMinimumShared(Number(event.target.value))}
              aria-label="Minimum shared funders"
              className="mt-1 w-48"
            />
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={showExcluded} onChange={(event) => setShowExcluded(event.target.checked)} />
            <span>Show set-aside intermediaries</span>
          </label>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find in map" aria-label="Find in map" className="rounded border border-stone-300 px-2 py-1" />
          <span className="text-stone-500" data-testid="map-count">
            {formatCount(layout.members.length)} of {formatCount(map.members.length)} organizations shown{layout.hiddenMembers > 0 ? `; ${formatCount(layout.hiddenMembers)} below the minimum` : ''}
          </span>
        </div>
      )}
      {compact ? null : <p className="mb-2 text-xs text-stone-500">{COPY.map.controls}</p>}
      {layout.members.length === 0 ? (
        <p className="mb-2 text-sm text-stone-600" data-testid="map-empty">
          No organization meets the minimum{query.length > 0 ? ' and the search' : ''}. Lower the minimum{query.length > 0 ? ' or clear the search' : ''} to see more.
        </p>
      ) : null}
      <div className={compact ? '' : 'md:grid md:grid-cols-[1fr_18rem] md:gap-6'}>
        <svg
          ref={drawing}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          className="hidden w-full md:block"
          role="group"
          aria-label={`Shared-funder map around ${layout.anchor.label}`}
          data-testid="map-svg"
        >
          {layout.edges.map((edge) => {
            const funder = byKey.get(edge.funder);
            const member = byKey.get(edge.member);
            if (funder === undefined || member === undefined) {
              return null;
            }
            const lit = incident(edge);
            return (
              <line
                key={`${edge.funder}-${edge.member}`}
                data-edge="true"
                data-active={lit ? 'true' : 'false'}
                x1={funder.x}
                y1={funder.y}
                x2={member.x}
                y2={member.y}
                stroke="#0f766e"
                strokeWidth={lit ? 1.5 : 1}
                strokeOpacity={active === null ? 0.35 : lit ? 1 : 0.08}
                className="motion-safe:transition-opacity"
              />
            );
          })}
          {layout.funders.map((funder) => (
            <line key={`a-${funder.key}`} x1={layout.anchor.x} y1={layout.anchor.y} x2={funder.x} y2={funder.y} stroke="#0f766e" strokeOpacity={active === null || active === ANCHOR_KEY || active === funder.key ? 0.35 : 0.06} strokeWidth={1} />
          ))}
          {layout.funders.map(node)}
          {layout.excluded.map(node)}
          {layout.members.map(node)}
          {node(layout.anchor)}
        </svg>
        <div className="md:hidden">
          <SharedFunderList layout={layout} active={active} onPin={setPinned} />
        </div>
        {compact ? null : (
          <aside className="mt-4 md:sticky md:top-4 md:mt-0 md:self-start" aria-label="Selected organization">
            <Detail layout={layout} node={activeNode} onExplore={onExplore} />
          </aside>
        )}
      </div>
    </div>
  );
};
